import { useState } from 'react'
import { BanknoteIcon, CreditCardIcon } from '../icons'
import { formatTRY } from '../../utils/money'
import { AmountField } from './AmountField'

interface Props {
  /** What the ticket itself comes to. The extra is on top of this. */
  totalKurus: number
  /** What the money on top is called on screen ("Bahşiş"). */
  label: string
  onCancel: () => void
  onConfirm: (bahsisKurus: number, method: 'cash' | 'card') => void
}

/** Turns "7000" or "7.000,50" into kuruş, or null if it is not a number. */
export function parseAmount(text: string): number | null {
  const cleaned = text.trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.')
  if (!cleaned) return null
  const value = Number(cleaned)
  if (!Number.isFinite(value) || value < 0) return null
  return Math.round(value * 100)
}

/**
 * The money left on top of a bill: the customer who hands over 7000 on a
 * 6000 ticket and says keep it: a tip, recorded on its own line.
 *
 * Asked as "how much was handed over" rather than "how much is left over",
 * because that is the number the cashier is holding, working the difference
 * out in their head at the counter is where the mistake would come from. The
 * remainder is shown back as it is typed so the two figures can be checked
 * against the money in hand before anything is recorded.
 */
export function BahsisPanel({ totalKurus, label, onCancel, onConfirm }: Props) {
  const [given, setGiven] = useState('')

  const givenKurus = parseAmount(given)
  const bahsisKurus = givenKurus === null ? null : givenKurus - totalKurus

  // Below the total is a mistyped figure, not a donation of a negative
  // amount; equal to it is a plain payment and belongs to the other buttons.
  const problem =
    givenKurus === null
      ? null
      : bahsisKurus! < 0
        ? 'Verilen tutar adisyondan az.'
        : bahsisKurus === 0
          ? 'Üstü kalmıyor, normal ödemeyi kullanın.'
          : null

  const ready = bahsisKurus !== null && bahsisKurus > 0

  return (
    <div className="bahsis" role="group" aria-label={label}>
      <div className="bahsis__head">
        <span className="bahsis__q">Ne kadar verildi?</span>
        <span className="bahsis__bill">Adisyon {formatTRY(totalKurus)}</span>
      </div>

      <AmountField
        value={given}
        onChange={setGiven}
        // Telefonda klavye açılınca panel (yazılan tutar ve altındaki bahşiş
        // satırı) görünür alana kaysın; klavyenin açılmasını bekleyerek.
        onFocus={(e) => {
          const panel = e.currentTarget.closest('.bahsis')
          window.setTimeout(() => panel?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 350)
        }}
        ariaLabel="Verilen tutar"
      />

      {/* The donation, worked out and shown back rather than asked for. */}
      <div className="bahsis__out" aria-live="polite">
        {problem ? (
          <span className="bahsis__warn">{problem}</span>
        ) : ready ? (
          /* Both figures, because one press records both: the cashier should
             see what lands where before choosing how it was handed over. */
          <>
            <span>Adisyon {formatTRY(totalKurus)},</span>
            <span>
              {label} <b>{formatTRY(bahsisKurus!)}</b>
            </span>
          </>
        ) : (
          <span className="bahsis__hint">Adisyondan fazlasını yazın.</span>
        )}
      </div>

      {/* One answer covers the whole handover, the bill and what was left on
          top of it. Someone paying 7000 on a 6000 ticket pays it once, in one
          form; asking twice would be a second step at the counter for a
          distinction that almost never exists. */}
      <div className="bahsis__opts">
        <button
          type="button"
          className="bahsis__opt"
          disabled={!ready}
          onClick={() => onConfirm(bahsisKurus!, 'cash')}
        >
          <BanknoteIcon />
          Nakit alındı
        </button>
        <button
          type="button"
          className="bahsis__opt"
          disabled={!ready}
          onClick={() => onConfirm(bahsisKurus!, 'card')}
        >
          <CreditCardIcon />
          Kartla alındı
        </button>
      </div>

      <button type="button" className="bahsis__cancel" onClick={onCancel}>
        Vazgeç
      </button>
    </div>
  )
}
